import React, { useMemo } from 'react';
import { ArrowRight, TrendingUp } from 'lucide-react';
import { useUserData } from '../context/UserDataContext';
import { runMonteCarlo } from '../utils/monteCarlo';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtC = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(n);

// ── Derive monthly savings from budget transactions / categories ──────────────
function getMonthlyContrib(budgetData) {
  const income = budgetData?.income || 0;
  if (income <= 0) return 0;
  const txns = budgetData?.transactions || [];
  const cats  = budgetData?.categories  || [];
  let spent = 0;
  if (txns.length > 0) {
    const now = new Date();
    const mStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const mEnd   = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    spent = txns
      .filter(t => { const d = new Date(t.date + 'T12:00:00'); return t.kind === 'expense' && d >= mStart && d <= mEnd; })
      .reduce((s, t) => s + t.amount, 0);
  } else {
    spent = cats.reduce((s, c) => s + (c.spent || 0), 0);
  }
  return Math.max(0, income - spent);
}

// ── Status helpers ─────────────────────────────────────────────────────────────
function getStatus(rate) {
  if (rate >= 90) return { label: 'On Track',       color: 'text-emerald-400', bg: 'bg-emerald-500/20 border-emerald-500/40', bar: 'bg-emerald-400' };
  if (rate >= 75) return { label: 'Moderate Risk',  color: 'text-amber-400',   bg: 'bg-amber-500/20 border-amber-500/40',   bar: 'bg-amber-400'   };
  if (rate >= 50) return { label: 'At Risk',         color: 'text-orange-400',  bg: 'bg-orange-500/20 border-orange-500/40', bar: 'bg-orange-400'  };
  return               { label: 'Needs Attention',  color: 'text-rose-400',    bg: 'bg-rose-500/20 border-rose-500/40',     bar: 'bg-rose-400'    };
}

// ── Sub-components ─────────────────────────────────────────────────────────────
function ScenarioCard({ label, rate, amount, accent }) {
  return (
    <div className={`flex-1 rounded-xl p-3.5 border ${accent.bg}`}>
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      <div className={`text-lg font-bold tabular-nums ${accent.color}`}>{rate}%</div>
      {amount != null && (
        <div className="text-xs text-slate-500 mt-0.5">{fmtC(amount)} median</div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function RetirementOutlook({ setActiveTab }) {
  const { retirementData, userProfile, budgetData, linkedAccountsData } = useUserData();

  // Pull current balance from retirement accounts or linkedAccounts
  const currentBalance = useMemo(() => {
    const retAccounts = retirementData?.accounts || [];
    if (retAccounts.length > 0) {
      return retAccounts.reduce((s, a) => s + (parseFloat(a.balance) || 0), 0);
    }
    const RETIREMENT_TYPES = ['401k', 'roth_ira', 'ira'];
    return (linkedAccountsData || [])
      .filter(a => RETIREMENT_TYPES.includes(a.type))
      .reduce((s, a) => s + (a.balance || 0), 0);
  }, [retirementData, linkedAccountsData]);

  const age            = userProfile?.age            || 30;
  const retirementAge  = userProfile?.retirementAge  || 65;
  const lifeExpectancy = userProfile?.lifeExpectancy || 90;
  const yearsToRetire  = Math.max(1, retirementAge - age);
  const yearsInRetire  = Math.max(1, lifeExpectancy - retirementAge);

  const monthlyContrib     = getMonthlyContrib(budgetData);
  const annualIncome       = (budgetData?.income || 0) * 12;
  const monthlyWithdrawal  = annualIncome > 0 ? (annualIncome * 0.8) / 12 : 4000; // 80% income replacement

  // Run base simulation (7% return, 15% vol)
  const base = useMemo(() => {
    if (currentBalance === 0 && monthlyContrib === 0) return null;
    const accum = runMonteCarlo({
      currentBalance,
      monthlyContrib,
      annualReturn: 7,
      annualVolatility: 15,
      years: yearsToRetire,
      simulations: 500,
    });
    if (accum.median <= 0) return null;
    const distrib = runMonteCarlo({
      currentBalance: accum.median,
      monthlyContrib: 0,
      annualReturn: 6,
      annualVolatility: 10,
      years: yearsInRetire,
      simulations: 500,
      monthlyWithdrawal,
    });
    return { accumMedian: accum.median, successRate: distrib.successRate };
  }, [currentBalance, monthlyContrib, yearsToRetire, yearsInRetire, monthlyWithdrawal]);

  // Conservative scenario (5% return, 12% vol)
  const conservative = useMemo(() => {
    if (!base) return null;
    const accum = runMonteCarlo({ currentBalance, monthlyContrib, annualReturn: 5, annualVolatility: 12, years: yearsToRetire, simulations: 300 });
    const distrib = runMonteCarlo({ currentBalance: accum.median, monthlyContrib: 0, annualReturn: 4.5, annualVolatility: 8, years: yearsInRetire, simulations: 300, monthlyWithdrawal });
    return { rate: distrib.successRate, amount: accum.median };
  }, [base, currentBalance, monthlyContrib, yearsToRetire, yearsInRetire, monthlyWithdrawal]);

  // Optimistic scenario (9% return, 18% vol)
  const optimistic = useMemo(() => {
    if (!base) return null;
    const accum = runMonteCarlo({ currentBalance, monthlyContrib, annualReturn: 9, annualVolatility: 18, years: yearsToRetire, simulations: 300 });
    const distrib = runMonteCarlo({ currentBalance: accum.median, monthlyContrib: 0, annualReturn: 7, annualVolatility: 12, years: yearsInRetire, simulations: 300, monthlyWithdrawal });
    return { rate: distrib.successRate, amount: accum.median };
  }, [base, currentBalance, monthlyContrib, yearsToRetire, yearsInRetire, monthlyWithdrawal]);

  // Use Retirement.js result if available (more authoritative, user already ran it)
  const successRate = retirementData?.successRate > 0
    ? retirementData.successRate
    : (base?.successRate ?? null);

  const hasData = successRate !== null;
  const status  = hasData ? getStatus(successRate) : null;

  // "What would move the needle" tips
  const tips = useMemo(() => {
    if (!hasData || successRate >= 90) return [];
    const list = [];
    if (monthlyContrib < 500) list.push('Contribute at least $500/month to retirement accounts');
    if (currentBalance < 50000 && age < 40) list.push('Focus on building your retirement balance early — compounding matters most now');
    if (successRate < 70) list.push('Consider working 2–3 years longer — it meaningfully reduces withdrawal years');
    if (monthlyWithdrawal > 5000) list.push('Plan to spend 70–80% of pre-retirement income, not 100%');
    list.push('Maximize tax-advantaged accounts (401k, Roth IRA) before taxable investing');
    return list.slice(0, 3);
  }, [hasData, successRate, monthlyContrib, currentBalance, age, monthlyWithdrawal]);

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="font-semibold text-slate-200 text-base">Retirement Outlook</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {hasData
              ? `${yearsToRetire}y to retirement · ${yearsInRetire}y distribution · Monte Carlo`
              : 'Add retirement data to see projections'}
          </p>
        </div>
        {hasData && status && (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border flex-shrink-0 ${status.bg} ${status.color}`}>
            {status.label}
          </span>
        )}
      </div>

      {!hasData ? (
        <div className="text-center py-6">
          <div className="text-3xl mb-3">📊</div>
          <p className="text-slate-400 text-sm mb-4 leading-relaxed">
            Add your retirement accounts and run the planner to see your projected success rate.
          </p>
          <button
            onClick={() => setActiveTab('retirement')}
            className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors"
          >
            Open Retirement Planner <ArrowRight size={14} />
          </button>
        </div>
      ) : (
        <>
          {/* Big rate + bar */}
          <div className="mb-5">
            <div className="flex items-end gap-3 mb-2">
              <span className={`text-5xl font-bold tabular-nums leading-none ${status.color}`}>
                {successRate}%
              </span>
              <div className="pb-1">
                <div className="text-sm text-slate-300 font-medium">success rate</div>
                <div className="text-xs text-slate-500">funds last through age {lifeExpectancy}</div>
              </div>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${status.bar}`}
                style={{ width: `${Math.min(successRate, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-slate-600 mt-1">
              <span>0%</span>
              <span className="text-slate-500">Target: 85%+</span>
              <span>100%</span>
            </div>
          </div>

          {/* Scenario cards */}
          {conservative && optimistic && (
            <div className="flex gap-3 mb-5">
              <ScenarioCard
                label="Conservative"
                rate={conservative.rate}
                amount={conservative.amount}
                accent={getStatus(conservative.rate)}
              />
              <ScenarioCard
                label="Base Case"
                rate={successRate}
                amount={base?.accumMedian ?? null}
                accent={status}
              />
              <ScenarioCard
                label="Optimistic"
                rate={optimistic.rate}
                amount={optimistic.amount}
                accent={getStatus(optimistic.rate)}
              />
            </div>
          )}

          {/* Key stats */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="bg-slate-700/40 rounded-lg p-3 text-center">
              <div className="text-xs text-slate-500 mb-1">Current Balance</div>
              <div className="text-sm font-bold text-slate-200">{currentBalance > 0 ? fmtC(currentBalance) : '—'}</div>
            </div>
            <div className="bg-slate-700/40 rounded-lg p-3 text-center">
              <div className="text-xs text-slate-500 mb-1">Monthly Saving</div>
              <div className="text-sm font-bold text-slate-200">{monthlyContrib > 0 ? fmt(monthlyContrib) : '—'}</div>
            </div>
            <div className="bg-slate-700/40 rounded-lg p-3 text-center">
              <div className="text-xs text-slate-500 mb-1">Retire At</div>
              <div className="text-sm font-bold text-slate-200">Age {retirementAge}</div>
            </div>
          </div>

          {/* What would move the needle */}
          {tips.length > 0 && (
            <div className="border-t border-slate-700/60 pt-4">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                <TrendingUp size={12} /> What Would Help
              </div>
              <div className="space-y-2">
                {tips.map((tip, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-slate-400 leading-relaxed">
                    <span className="text-blue-400 mt-0.5 flex-shrink-0">→</span>
                    {tip}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer CTA */}
          <div className="mt-4 pt-3 border-t border-slate-700/40">
            <button
              onClick={() => setActiveTab('retirement')}
              className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 font-medium transition-colors"
            >
              Open full planner to adjust inputs <ArrowRight size={11} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
